import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertTriangle, Flag, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30000;

const severityStyle = (sev) => {
  if (sev === 'HIGH')   return 'bg-red-600 text-white ring-red-400';
  if (sev === 'MEDIUM') return 'bg-amber-500 text-white ring-amber-400';
  return 'bg-slate-500 text-white ring-slate-400';
};

const AdminAlertBell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ high: 0, medium: 0, low: 0, total: 0 });
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const timerRef = useRef(null);
  const popRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/trust/admin/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status_filter: 'open', limit: 8 },
      });
      const d = res.data;
      setAlerts(d.alerts || []);
      setCounts({
        high: d.open_high || 0,
        medium: d.open_medium || 0,
        low: d.open_low || 0,
        total: (d.open_high || 0) + (d.open_medium || 0) + (d.open_low || 0),
      });
      setIsAdmin(true);
    } catch (e) {
      // Silently hide if not admin or auth expired - the bell should never crash the page
      if (e.response?.status === 401 || e.response?.status === 403) {
        setIsAdmin(false);
      }
    }
  }, []);

  useEffect(() => {
    // Only show + poll on admin routes
    if (!location.pathname.startsWith('/admin')) {
      setIsAdmin(false);
      return;
    }
    fetchAlerts();
    timerRef.current = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [location.pathname, fetchAlerts]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isAdmin || !location.pathname.startsWith('/admin')) return null;
  const isLoginPage = location.pathname === '/admin/login';
  if (isLoginPage) return null;

  const hasHigh = counts.high > 0;
  const hasAny = counts.total > 0;

  return (
    <div
      ref={popRef}
      className="fixed z-[60] top-4 right-4"
      data-testid="admin-alert-bell"
    >
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (!open) fetchAlerts(); }}
        className={`relative flex items-center justify-center w-11 h-11 rounded-full shadow-lg transition-all
          ${hasHigh ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
            : hasAny ? 'bg-amber-500 hover:bg-amber-600 text-white'
            : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-300'}
        `}
        data-testid="alert-bell-button"
        aria-label={`${counts.total} open trust alerts`}
      >
        <Bell className="h-5 w-5" />
        {hasAny && (
          <span
            className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center
              text-[11px] font-bold ring-2 ring-white ${severityStyle(hasHigh ? 'HIGH' : counts.medium > 0 ? 'MEDIUM' : 'LOW')}`}
            data-testid="alert-bell-count"
          >
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="alert-bell-popover"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${hasHigh ? 'text-red-600' : 'text-amber-500'}`} />
              <span className="font-semibold text-sm text-slate-800">Trust alerts</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Severity summary */}
          <div className="grid grid-cols-3 gap-0 border-b">
            {[
              { label: 'HIGH', value: counts.high, tone: 'text-red-700 bg-red-50' },
              { label: 'MEDIUM', value: counts.medium, tone: 'text-amber-700 bg-amber-50' },
              { label: 'LOW', value: counts.low, tone: 'text-slate-700 bg-slate-50' },
            ].map((s) => (
              <div key={s.label} className={`px-3 py-2 text-center ${s.tone}`} data-testid={`bell-count-${s.label}`}>
                <div className="text-xs font-medium uppercase tracking-wide">{s.label}</div>
                <div className="text-lg font-bold">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Alert list */}
          <div className="max-h-72 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">
                <Flag className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
                Queue clear. No open alerts.
              </div>
            ) : alerts.map((a) => (
              <button
                key={a.id}
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b last:border-b-0 flex items-start gap-2"
                onClick={() => {
                  setOpen(false);
                  navigate('/admin/repeat-offenders');
                }}
                data-testid={`bell-alert-${a.id}`}
              >
                <span
                  className={`shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full ${
                    a.severity === 'HIGH' ? 'bg-red-600'
                    : a.severity === 'MEDIUM' ? 'bg-amber-500'
                    : 'bg-slate-400'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {a.severity} · {a.kind}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {a.message}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {a.user?.email || a.user_id?.slice(0, 8)}
                    {a.created_at && ` · ${new Date(a.created_at).toLocaleTimeString()}`}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/admin/repeat-offenders'); }}
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
            data-testid="bell-view-all"
          >
            View all in Repeat Offenders →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminAlertBell;
